import { FundCode } from "@repo/api";
import { EnvironmentHelper } from "@repo/helpers";
import { AMLData, LocalStorageKeys } from "@repo/logic";
import { cs } from "@repo/ui";
import { type cookieId } from "./components/Drawer/CookieDrawer/CookieIds";
import { type ICookieConsent } from "./context/CookieContext";

export function formatAmount(amount: number): string {
    const rounded = Math.round(amount * 100) / 100;
    const formatted = rounded
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, "\xa0")
        .replace(/\./g, ",");
    return formatted;
}

export function formatAmountWithCurrencyCode(amount: number, currencyCode: "CZK" | "EUR" = "CZK"): string {
    return `${formatAmount(amount)} ${cs.currencies[currencyCode]}`;
}

export const getViewportDimensions = () => {
    const isClient = EnvironmentHelper.isBrowser();
    return {
        width: isClient ? window.innerWidth : 0,
        height: isClient ? window.innerHeight : 0,
    };
};

export const debounce = (fn, ms) => {
    let timer;
    return () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
            timer = null;
            fn.apply(this);
        }, ms);
    };
};

export const sortedFunds = <T extends { fundCode: FundCode; allocation: number }>(arr?: T[]): T[] | null => {
    if (arr === undefined) {
        return null;
    }
    return arr
        .reduce((acc: T[], obj: T): T[] => {
            const insertIndex = acc.findIndex((item) => item.fundCode > obj.fundCode);
            if (insertIndex === -1) {
                acc.push(obj);
            } else {
                acc.splice(insertIndex, 0, obj);
            }
            return acc;
        }, [])
        .sort((a: T, b: T): number => {
            if (a.fundCode === FundCode.Grow) {
                return -1;
            }
            if (b.fundCode === FundCode.Grow) {
                return 1;
            }
            if (a.fundCode === FundCode.Bal) {
                return -1;
            }
            if (b.fundCode === FundCode.Bal) {
                return 1;
            }
            if (a.fundCode === FundCode.Con) {
                return -1;
            }
            if (b.fundCode === FundCode.Con) {
                return 1;
            }
            return 0;
        });
};

export function countryCode2CountryName(code: string) {
    const countryName = AMLData.Countries.find((country) => country.country_code_a3 === code)?.country_name_cz;

    return code === "CZ" ? "Česká Republika" : countryName;
}

/**
 * create img from url for qr code
 */
export async function downloadImage(imageSrc: string) {
    const image = await fetch(imageSrc);
    const imageBlog = await image.blob();
    const imageURL = URL.createObjectURL(imageBlog);

    const link = document.createElement("a");
    link.href = imageURL;
    link.download = "qr-kod-pro-platbu";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

export const getAllowedCookiesFromLocalStorage = (): cookieId[] => {
    const cookieConsentJSON = EnvironmentHelper.isBrowser()
        ? window.localStorage.getItem(LocalStorageKeys.COOKIE_CONSENT)
        : null;

    if (cookieConsentJSON === null) {
        return [];
    }

    return (JSON.parse(cookieConsentJSON) as ICookieConsent).allowedCookies;
};
